import cx from 'classnames'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { BrandPlanTactic, SelectedBlogPlatform, SelectedEmailPlatform, ContentCount, CrosspostFrequency } from 'types'
import { useMemo, useState } from 'react'
import { Formik } from 'formik'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import AutopilotRegistrationAndOnboardingStyles from 'screens/AutopilotRegistration/AutopilotRegistrationAndOnboarding.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Checkbox, Label as CheckboxLabel, Box as CheckboxBox } from 'blaze-ui/Checkbox/Checkbox'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import BrandPlanTacticsStyles from './BrandPlanTactics.module.scss'
import Styles from './BrandPlanTacticsRedesign.module.scss'
import { Loading } from 'screens/VirtualMarketerOnboarding/Loading/Loading'
import { CountInput } from 'components/Wizards/CountInput/CountInput'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useUpdateBrandProfile } from 'hooks/queries/brandProfiles/useUpdateBrandProfile'
import { useCreateBrandPlanPreparationSync } from 'hooks/queries/brandPlans/useCreateBrandPlanPreparationSync'
import { useProgress } from 'hooks/useProgress'
import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import { BrandPlan } from 'types'
import dayjs from 'dayjs'
import { useRef } from 'react'
import { Select } from 'blaze-ui/Select/Select'
import { Item, Section } from '@react-stately/collections'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { InstagramBrand } from 'blaze-ui/icons/24'
import {
  FacebookBrand,
  WordPressBrand,
  MailchimpBrand,
  GoHighLevelBrand,
  WixBrand,
  ShopifyBrand,
} from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { InstagramReelsBrand } from 'blaze-ui/icons/30'
import { TikTokBrand, YouTubeBrand, TwitterBrand, MetaBrand, LinkExternal } from 'blaze-ui/icons'
import XSquareContained from 'blaze-ui/icons/24/XSquareContained'
import { WeeklyBudgetInput } from './WeeklyBudgetInput/WeeklyBudgetInput'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ChannelIntegrationButton } from './ChannelIntegrationButton'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useHover } from '@react-aria/interactions'

const SelectItem = ({ icon: Icon, children }: { icon: any; children: React.ReactNode }) => (
  <div className={Styles.selectItem}>
    <Icon size={20} />
    <Text color="inherit">{children}</Text>
  </div>
)

interface MetaAdsCheckboxProps {
  isSelected: boolean
  onChange: () => void
}

const MetaAdsCheckbox = ({ isSelected, onChange }: MetaAdsCheckboxProps) => {
  const { isHovered, hoverProps } = useHover({})
  const integrations = useExternalIntegrations()
  const matchingIntegrations =
    integrations.data?.external_integrations?.filter(
      (integration: ExternalIntegration) => integration.provider === ExternalIntegrationEnum.MetaAds,
    ) || []

  const hasIntegrations = matchingIntegrations.length > 0

  return (
    <div {...hoverProps} className={Styles.metaAdsWrapper}>
      <CheckboxLabel
        className={Styles.platformCheckbox}
        isSelected={isSelected}
        onChange={onChange}
        aria-label="Meta Ads"
      >
        <CheckboxBox size={20} color="var(--dark-80)" aria-label="Meta Ads" />
        <div className={Styles.metaAdsContent}>
          <div className={Styles.channelLabel}>
            <MetaBrand size={20} />
            <Text variant="primary">Meta</Text>
          </div>
          <div className={cx(Styles.integrationButtonContainer, { [Styles.visible]: isHovered || hasIntegrations })}>
            <ChannelIntegrationButton
              integrations={matchingIntegrations}
              isHovered={true}
              provider={ExternalIntegrationEnum.MetaAds}
              isDisabled={false}
              showDisabledLabel={false}
            />
          </div>
        </div>
      </CheckboxLabel>
    </div>
  )
}

const buildInstagramContents = (
  postCount: number = 0,
  storyCount: number = 1,
  reelCount: number = 0,
): ContentCount[] => [
  { type: 'post', count: postCount },
  { type: 'story', count: storyCount },
  { type: 'reel', count: reelCount },
]

const PLATFORM_TOGGLE_CONFIG: Record<
  BrandPlanTactic,
  {
    contentField: keyof FormValues
    buildAddContent: (formValues: FormValues) => ContentCount[]
    buildRemoveContent: (formValues: FormValues) => ContentCount[]
  } | null
> = {
  [BrandPlanTactic.Instagram]: {
    contentField: 'instagram_contents',
    buildAddContent: () => buildInstagramContents(),
    buildRemoveContent: () => [],
  },
  [BrandPlanTactic.Facebook]: {
    contentField: 'facebook_contents',
    buildAddContent: () => [{ type: 'post', count: 0 }],
    buildRemoveContent: () => [],
  },
  [BrandPlanTactic.LinkedIn]: {
    contentField: 'linkedin_contents',
    buildAddContent: () => [{ type: 'post', count: 0 }],
    buildRemoveContent: () => [],
  },
  [BrandPlanTactic.Twitter]: {
    contentField: 'twitter_contents',
    buildAddContent: () => [{ type: 'post', count: 0 }],
    buildRemoveContent: () => [],
  },
  [BrandPlanTactic.InstagramReel]: {
    contentField: 'instagram_contents',
    buildAddContent: (formValues) => {
      const currentInstagram = formValues.instagram_contents
      return buildInstagramContents(currentInstagram[0]?.count || 0, currentInstagram[1]?.count || 1, 0)
    },
    buildRemoveContent: (formValues) => {
      const instagramStillSelected = formValues.tactics.includes(BrandPlanTactic.Instagram)
      if (instagramStillSelected) {
        const currentInstagram = formValues.instagram_contents
        return buildInstagramContents(currentInstagram[0]?.count || 0, currentInstagram[1]?.count || 1, 0)
      }
      return []
    },
  },
  [BrandPlanTactic.TikTok]: {
    contentField: 'tiktok_contents',
    buildAddContent: () => [{ type: 'post', count: 0 }],
    buildRemoveContent: () => [],
  },
  [BrandPlanTactic.YouTube]: {
    contentField: 'youtube_contents',
    buildAddContent: () => [{ type: 'short', count: 0 }],
    buildRemoveContent: () => [],
  },
  [BrandPlanTactic.SEO]: null,
  [BrandPlanTactic.Email]: null,
  [BrandPlanTactic.MetaAds]: null,
}

interface PlatformCheckboxProps {
  tactic: BrandPlanTactic
  icon: React.ComponentType<{ size?: number }>
  label: string
  isSelected: boolean
  onToggle: (tactic: BrandPlanTactic) => void
}

const PlatformCheckbox = ({ tactic, icon: Icon, label, isSelected, onToggle }: PlatformCheckboxProps) => (
  <Checkbox isSelected={isSelected} onChange={() => onToggle(tactic)}>
    <div className={Styles.channelLabel}>
      <Icon size={20} />
      <Text variant="primary">{label}</Text>
    </div>
  </Checkbox>
)

const SOCIAL_PLATFORMS = [
  { tactic: BrandPlanTactic.Instagram, icon: InstagramBrand, label: 'Instagram Posts' },
  { tactic: BrandPlanTactic.LinkedIn, icon: LinkedInBrand, label: 'LinkedIn Posts' },
  { tactic: BrandPlanTactic.Facebook, icon: FacebookBrand, label: 'Facebook Posts' },
  { tactic: BrandPlanTactic.Twitter, icon: TwitterBrand, label: 'X Posts' },
]

const VIDEO_PLATFORMS = [
  { tactic: BrandPlanTactic.TikTok, icon: TikTokBrand, label: 'TikTok' },
  { tactic: BrandPlanTactic.YouTube, icon: YouTubeBrand, label: 'YouTube' },
  { tactic: BrandPlanTactic.InstagramReel, icon: InstagramReelsBrand, label: 'Instagram Reels' },
]

interface PlatformSelectDropdownProps {
  type: 'blog' | 'email'
  isSelected: boolean
  selectedPlatform: string | null
  defaultPlatform: string
  platforms: Array<{ value: string; label: string; icon: any }>
  noneLabel: string
  onSelectionChange: (value: string) => void
}

const PlatformSelectDropdown = ({
  type,
  isSelected,
  selectedPlatform,
  defaultPlatform,
  platforms,
  noneLabel,
  onSelectionChange,
}: PlatformSelectDropdownProps) => {
  const renderValue = (selectedItem: any) => {
    if (!selectedItem) return null
    const platformOptions = Object.fromEntries(
      platforms.filter((p) => p.icon).map((p) => [p.value, { label: p.label, icon: p.icon }]),
    )
    return renderPlatformSelectValue(isSelected, selectedPlatform, defaultPlatform, platformOptions, noneLabel)
  }

  return (
    <Select
      selectedKey={isSelected ? selectedPlatform || defaultPlatform : 'none'}
      onSelectionChange={(key) => onSelectionChange(String(key))}
      variant="secondary"
      size="lg"
      aria-label={`${type} platform selection`}
      renderSelectValue={renderValue}
    >
      <Section>
        {platforms.map(({ value, label, icon: PlatformIcon }) => (
          <Item key={value} textValue={label}>
            {PlatformIcon ? (
              <SelectItem icon={PlatformIcon}>{label}</SelectItem>
            ) : (
              <div className={BrandPlanTacticsStyles.selectItemWrapper}>
                <SelectItem icon={LinkExternal}>{label}</SelectItem>
                <div className={BrandPlanTacticsStyles.selectItemSubtext}>
                  <span className={BrandPlanTacticsStyles.selectItemSubtextContent}>
                    We'll still create {type} posts for you, but you'll need to publish them yourself.
                  </span>
                </div>
              </div>
            )}
          </Item>
        ))}
      </Section>
      <Section>
        <Item key="none" textValue={noneLabel}>
          <SelectItem icon={XSquareContained}>{noneLabel}</SelectItem>
        </Item>
      </Section>
    </Select>
  )
}

const renderPlatformSelectValue = (
  isSelected: boolean,
  selectedPlatform: string | null,
  defaultPlatform: string,
  platformOptions: Record<string, { label: string; icon: any }>,
  noneLabel: string,
) => {
  const currentKey = isSelected ? selectedPlatform || defaultPlatform : 'none'

  if (platformOptions[currentKey]) {
    const option = platformOptions[currentKey]
    return <SelectItem icon={option.icon}>{option.label}</SelectItem>
  }

  if (currentKey === 'generic') {
    return <SelectItem icon={LinkExternal}>I'm using another platform</SelectItem>
  }

  if (currentKey === 'none') {
    return <SelectItem icon={XSquareContained}>{noneLabel}</SelectItem>
  }

  return null
}

interface BrandPlanTacticsRedesignContainerProps {
  id: number
  tactics: BrandPlanTactic[]
  seoKeywords: string[]
  idealCustomers: string[]
  mode: 'starter'
  onBack: () => void
  onNext: (data: {
    tactics: BrandPlanTactic[]
    seoKeywords: string[]
    idealCustomers: string[]
    selectedBlogPlatform?: SelectedBlogPlatform
    selectedEmailPlatform?: SelectedEmailPlatform
    paidAdsOnboardingParameters?: {
      weeklyAdsBudget?: number
      selectedPlatforms: any[]
    }
  }) => void
}

interface BrandPlanTacticsRedesignProps extends Omit<BrandPlanTacticsRedesignContainerProps, 'id'> {
  brandPlan: BrandPlan
}

const SOCIAL_MEDIA_TACTICS: BrandPlanTactic[] = [
  BrandPlanTactic.Instagram,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Twitter,
]

const VIDEO_TACTICS = [BrandPlanTactic.InstagramReel, BrandPlanTactic.TikTok, BrandPlanTactic.YouTube]

const TACTIC_TO_PROVIDER: Record<string, ExternalIntegrationEnum> = {
  [BrandPlanTactic.Instagram]: ExternalIntegrationEnum.Instagram,
  [BrandPlanTactic.Facebook]: ExternalIntegrationEnum.Facebook,
  [BrandPlanTactic.LinkedIn]: ExternalIntegrationEnum.LinkedIn,
  [BrandPlanTactic.Twitter]: ExternalIntegrationEnum.X,
  [BrandPlanTactic.MetaAds]: ExternalIntegrationEnum.MetaAds,
}

const DEFAULT_SCHEDULE_CONFIG = {
  week_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  days_before_review: 3,
  daily_frequency: 'once_a_day',
  length: 6,
  schedule_length: 1,
} as const

const DEFAULT_CONTENTS = {
  crosspost: [{ type: 'post', count: 4 }] as ContentCount[],
  video_crosspost: [{ type: 'post', count: 3 }] as ContentCount[],
  instagram: [
    { type: 'post', count: 0 },
    { type: 'story', count: 1 },
    { type: 'reel', count: 0 },
  ] as ContentCount[],
  facebook: [{ type: 'post', count: 0 }] as ContentCount[],
  linkedin: [{ type: 'post', count: 0 }] as ContentCount[],
  twitter: [{ type: 'post', count: 0 }] as ContentCount[],
  tiktok: [{ type: 'post', count: 0 }] as ContentCount[],
  youtube: [{ type: 'short', count: 0 }] as ContentCount[],
  seo: [{ type: 'blog_post', count: 2 }] as ContentCount[],
  email: [{ type: 'newsletter', count: 2 }] as ContentCount[],
}

interface FormValues {
  crosspost_frequency: CrosspostFrequency
  crosspost_contents: ContentCount[]
  video_crosspost_contents: ContentCount[]
  instagram_contents: ContentCount[]
  facebook_contents: ContentCount[]
  linkedin_contents: ContentCount[]
  twitter_contents: ContentCount[]
  tiktok_contents: ContentCount[]
  youtube_contents: ContentCount[]
  seo_contents?: ContentCount[]
  email_contents?: ContentCount[]
  tactics: BrandPlanTactic[]
  weekly_ads_budget?: number
}
const PLATFORM_CONFIGS = {
  blog: {
    defaultPlatform: ExternalIntegrationEnum.WordPress,
    platforms: (wixEnabled: boolean = false, shopifyEnabled: boolean = false) => [
      { value: ExternalIntegrationEnum.WordPress, label: 'WordPress', icon: WordPressBrand },
      { value: ExternalIntegrationEnum.GoHighLevel, label: 'GoHighLevel', icon: GoHighLevelBrand },
      ...(wixEnabled ? [{ value: ExternalIntegrationEnum.Wix, label: 'Wix', icon: WixBrand }] : []),
      ...(shopifyEnabled ? [{ value: ExternalIntegrationEnum.Shopify, label: 'Shopify', icon: ShopifyBrand }] : []),
      { value: 'generic', label: "I'm using another platform", icon: null },
    ],
  },
  email: {
    defaultPlatform: ExternalIntegrationEnum.Mailchimp,
    platforms: () => [
      { value: ExternalIntegrationEnum.Mailchimp, label: 'Mailchimp', icon: MailchimpBrand },
      { value: 'generic', label: "I'm using another platform", icon: null },
    ],
  },
} as const

const getContentOrDefault = (
  content: ContentCount[] | undefined,
  defaultContent: ContentCount[],
  fieldName: string,
): ContentCount[] => {
  if (!content || content.length === 0) {
    return defaultContent
  }

  const allZero = content.every((c) => (c.count || 0) === 0)
  const defaultsHaveNonZero = defaultContent.some((c) => (c.count || 0) > 0)

  if (allZero && defaultsHaveNonZero) {
    return defaultContent
  }

  return content
}

const deriveTacticsFromContentArrays = (contentValues: {
  crosspost_contents: ContentCount[]
  video_crosspost_contents: ContentCount[]
  instagram_contents: ContentCount[]
  facebook_contents: ContentCount[]
  linkedin_contents: ContentCount[]
  twitter_contents: ContentCount[]
  tiktok_contents: ContentCount[]
  youtube_contents: ContentCount[]
  seo_contents: ContentCount[]
  email_contents: ContentCount[]
}): BrandPlanTactic[] => {
  const tactics: BrandPlanTactic[] = []

  const hasSocialCrosspost = contentValues.crosspost_contents?.some((c) => c.count > 0)
  if (hasSocialCrosspost) {
    tactics.push(...SOCIAL_MEDIA_TACTICS)
  }

  const hasVideoCrosspost = contentValues.video_crosspost_contents?.some((c) => c.count > 0)
  if (hasVideoCrosspost) {
    tactics.push(...VIDEO_TACTICS)
  }

  if (!hasSocialCrosspost || !hasVideoCrosspost) {
    const platformChecks = [
      {
        content: contentValues.instagram_contents,
        type: 'post',
        tactic: BrandPlanTactic.Instagram,
        skipIf: hasSocialCrosspost,
      },
      {
        content: contentValues.instagram_contents,
        type: 'reel',
        tactic: BrandPlanTactic.InstagramReel,
        skipIf: hasVideoCrosspost,
      },
      { content: contentValues.facebook_contents, tactic: BrandPlanTactic.Facebook, skipIf: hasSocialCrosspost },
      { content: contentValues.linkedin_contents, tactic: BrandPlanTactic.LinkedIn, skipIf: hasSocialCrosspost },
      { content: contentValues.twitter_contents, tactic: BrandPlanTactic.Twitter, skipIf: hasSocialCrosspost },
      { content: contentValues.tiktok_contents, tactic: BrandPlanTactic.TikTok, skipIf: hasVideoCrosspost },
      { content: contentValues.youtube_contents, tactic: BrandPlanTactic.YouTube, skipIf: hasVideoCrosspost },
    ]

    platformChecks.forEach(({ content, type, tactic, skipIf }) => {
      if (!skipIf && content?.some((c) => (!type || c.type === type) && c.count > 0)) {
        tactics.push(tactic)
      }
    })
  }

  if (contentValues.seo_contents?.some((c) => c.count > 0)) {
    tactics.push(BrandPlanTactic.SEO)
  }
  if (contentValues.email_contents?.some((c) => c.count > 0)) {
    tactics.push(BrandPlanTactic.Email)
  }

  return tactics
}

const getInitialValues = (brandPlan: BrandPlan): FormValues => {
  const contentValues = {
    crosspost_frequency: (brandPlan.crosspost_frequency || 'always') as CrosspostFrequency,
    crosspost_contents: getContentOrDefault(
      brandPlan.crosspost_contents,
      DEFAULT_CONTENTS.crosspost,
      'crosspost_contents',
    ),
    video_crosspost_contents: getContentOrDefault(
      brandPlan.video_crosspost_contents,
      DEFAULT_CONTENTS.video_crosspost,
      'video_crosspost_contents',
    ),
    instagram_contents: getContentOrDefault(
      brandPlan.instagram_contents,
      DEFAULT_CONTENTS.instagram,
      'instagram_contents',
    ),
    facebook_contents: getContentOrDefault(brandPlan.facebook_contents, DEFAULT_CONTENTS.facebook, 'facebook_contents'),
    linkedin_contents: getContentOrDefault(brandPlan.linkedin_contents, DEFAULT_CONTENTS.linkedin, 'linkedin_contents'),
    twitter_contents: getContentOrDefault(brandPlan.twitter_contents, DEFAULT_CONTENTS.twitter, 'twitter_contents'),
    tiktok_contents: getContentOrDefault(brandPlan.tiktok_contents, DEFAULT_CONTENTS.tiktok, 'tiktok_contents'),
    youtube_contents: getContentOrDefault(brandPlan.youtube_contents, DEFAULT_CONTENTS.youtube, 'youtube_contents'),
    seo_contents: getContentOrDefault(brandPlan.seo_contents, DEFAULT_CONTENTS.seo, 'seo_contents'),
    email_contents: getContentOrDefault(brandPlan.email_contents, DEFAULT_CONTENTS.email, 'email_contents'),
  }

  let finalTactics: BrandPlanTactic[]

  if (brandPlan.tactics && brandPlan.tactics.length > 0) {
    finalTactics = brandPlan.tactics
  } else {
    const derivedTactics = deriveTacticsFromContentArrays(contentValues)
    const defaultTactics = [...SOCIAL_MEDIA_TACTICS, ...VIDEO_TACTICS]
    finalTactics = derivedTactics.length > 0 ? derivedTactics : defaultTactics
  }

  return {
    ...contentValues,
    tactics: finalTactics,
    weekly_ads_budget: brandPlan.weekly_ads_budget || 0,
  }
}

const BrandPlanTacticsRedesign = (props: BrandPlanTacticsRedesignProps) => {
  if (props.mode !== 'starter') {
    return null
  }

  const { brandPlan, onBack, onNext, seoKeywords: initialSeoKeywords, idealCustomers: initialIdealCustomers } = props

  const initialValues = getInitialValues(brandPlan)

  return (
    <Formik initialValues={initialValues} onSubmit={() => {}} validateOnMount={false} enableReinitialize={true}>
      {({ values: formValues, setFieldValue }) => (
        <BrandPlanTacticsRedesignContent
          formValues={formValues}
          setFieldValue={setFieldValue}
          brandPlan={brandPlan}
          onBack={onBack}
          onNext={onNext}
          initialSeoKeywords={initialSeoKeywords}
          initialIdealCustomers={initialIdealCustomers}
        />
      )}
    </Formik>
  )
}

interface BrandPlanTacticsRedesignContentProps {
  formValues: FormValues
  setFieldValue: (field: string, value: any) => void
  brandPlan: BrandPlan
  onBack: () => void
  onNext: (data: any) => void
  initialSeoKeywords: string[]
  initialIdealCustomers: string[]
}

const BrandPlanTacticsRedesignContent = ({
  formValues,
  setFieldValue,
  brandPlan,
  onBack,
  onNext,
  initialSeoKeywords,
  initialIdealCustomers,
}: BrandPlanTacticsRedesignContentProps) => {
  const { isMobile } = useResponsiveDesign()
  const abortControllerRef = useRef<AbortController | null>(null)
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const shopifyEnabled = useFlipperFlag('shopify_integration')
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const {
    start: startThree,
    progress: progressThree,
    isRunning: isRunningThree,
    reset: resetThree,
  } = useProgress({
    linearProgress: true,
    immediateComplete: true,
    speed: 17,
  })

  const {
    start: startTwo,
    progress: progressTwo,
    isRunning: isRunningTwo,
    reset: resetTwo,
  } = useProgress({
    linearProgress: true,
    immediateComplete: true,
    speed: 17,
    onDone: startThree,
  })

  const {
    start: startOne,
    progress: progressOne,
    isRunning: isRunningOne,
    reset: resetOne,
  } = useProgress({
    linearProgress: true,
    immediateComplete: true,
    speed: 17,
    onDone: startTwo,
  })

  const { mutate: updateBrandPlan, isLoading: isUpdatingBrandPlan } = useUpdateBrandPlan({ id: brandPlan.id })
  const { mutate: updateBrandProfile } = useUpdateBrandProfile({ id: brandPlan.brand_profile.id })
  const { mutateAsync: createBrandPlanPreparationSync, isLoading: isPreparing } = useCreateBrandPlanPreparationSync({
    brandPlanId: brandPlan.id,
  })

  const [isBlogSelected, setIsBlogSelected] = useState(() => formValues.tactics.includes(BrandPlanTactic.SEO))
  const [isEmailSelected, setIsEmailSelected] = useState(() => formValues.tactics.includes(BrandPlanTactic.Email))
  const [selectedBlogPlatform, setSelectedBlogPlatform] = useState<SelectedBlogPlatform>(
    brandPlan.selected_blog_platform || ExternalIntegrationEnum.WordPress,
  )
  const [selectedEmailPlatform, setSelectedEmailPlatform] = useState<SelectedEmailPlatform>(
    brandPlan.selected_email_platform || ExternalIntegrationEnum.Mailchimp,
  )

  const socialTactics = formValues.tactics.filter((t) => SOCIAL_MEDIA_TACTICS.includes(t))
  const videoTactics = formValues.tactics.filter((t) => VIDEO_TACTICS.includes(t))
  const paidAdsTactics = formValues.tactics.filter((t) => t === BrandPlanTactic.MetaAds)

  const tactics = useMemo(() => {
    const activeTactics: BrandPlanTactic[] = []

    if (formValues.crosspost_contents.some((c) => c.count > 0)) {
      activeTactics.push(...socialTactics)
    }

    if (formValues.video_crosspost_contents.some((c) => c.count > 0)) {
      activeTactics.push(...videoTactics)
    }

    if (isBlogSelected && formValues.seo_contents?.some((c) => c.count > 0)) {
      activeTactics.push(BrandPlanTactic.SEO)
    }
    if (isEmailSelected && formValues.email_contents?.some((c) => c.count > 0)) {
      activeTactics.push(BrandPlanTactic.Email)
    }

    activeTactics.push(...paidAdsTactics)

    return Array.from(new Set(activeTactics))
  }, [
    formValues.crosspost_contents,
    formValues.video_crosspost_contents,
    formValues.seo_contents,
    formValues.email_contents,
    socialTactics,
    videoTactics,
    isBlogSelected,
    isEmailSelected,
    paidAdsTactics,
  ])

  const contentCounts = {
    social_media: formValues.crosspost_contents[0]?.count || 0,
    short_form_video: formValues.video_crosspost_contents[0]?.count || 0,
    blog: formValues.seo_contents?.[0]?.count || 0,
    email: formValues.email_contents?.[0]?.count || 0,
  }

  const weeklyAdsBudget = formValues.weekly_ads_budget || 0

  // Credit calculation temporarily disabled
  // const socialCount = socialTactics.length > 0 ? contentCounts.social_media : 0
  // const blogAndEmailCount = (isBlogSelected ? contentCounts.blog : 0) + (isEmailSelected ? contentCounts.email : 0)
  // const shortFormVideoCount = videoTactics.length > 0 ? contentCounts.short_form_video : 0

  // const { creditsNeeded: totalCredits } = useNeedsCreditsToGenerateContent(
  //   socialCount,
  //   blogAndEmailCount,
  //   shortFormVideoCount,
  // )

  const handlePlatformToggle = (tactic: BrandPlanTactic) => {
    const currentTactics = formValues.tactics
    const isAdding = !currentTactics.includes(tactic)
    const newTactics = isAdding ? [...currentTactics, tactic] : currentTactics.filter((t) => t !== tactic)
    setFieldValue('tactics', newTactics)

    const config = PLATFORM_TOGGLE_CONFIG[tactic]
    if (config) {
      const content = isAdding ? config.buildAddContent(formValues) : config.buildRemoveContent(formValues)
      setFieldValue(config.contentField, content)
    }
  }

  const updateCrosspostPlatformCounts = (count: number, crosspostField: string, platformTactics: BrandPlanTactic[]) => {
    setFieldValue(crosspostField, [{ type: 'post', count }])
    platformTactics.forEach((tactic) => {
      if (formValues.tactics.includes(tactic)) {
        const config = PLATFORM_TOGGLE_CONFIG[tactic]
        if (config) {
          const zeroContent = config.buildAddContent(formValues).map((c) => ({ ...c, count: 0 }))
          setFieldValue(config.contentField, zeroContent)
        }
      }
    })
  }

  const buildOnNextPayload = () => {
    return {
      tactics,
      seoKeywords: initialSeoKeywords,
      idealCustomers: initialIdealCustomers,
      selectedBlogPlatform: isBlogSelected ? selectedBlogPlatform : undefined,
      selectedEmailPlatform: isEmailSelected ? selectedEmailPlatform : undefined,
      paidAdsOnboardingParameters: {
        weeklyAdsBudget: paidAdsTactics.includes(BrandPlanTactic.MetaAds) ? weeklyAdsBudget : undefined,
        selectedPlatforms: paidAdsTactics.includes(BrandPlanTactic.MetaAds)
          ? [TACTIC_TO_PROVIDER[BrandPlanTactic.MetaAds]]
          : [],
      },
    }
  }

  const buildContentsPayload = () => {
    const payload: any = {}

    if (formValues.crosspost_contents.some((c) => c.count > 0)) {
      payload.crosspost_contents = formValues.crosspost_contents
    }
    if (formValues.video_crosspost_contents.some((c) => c.count > 0)) {
      payload.video_crosspost_contents = formValues.video_crosspost_contents
    }
    if (socialTactics.includes(BrandPlanTactic.Instagram)) {
      payload.instagram_contents = formValues.instagram_contents
    } else if (videoTactics.includes(BrandPlanTactic.InstagramReel)) {
      payload.instagram_contents = buildInstagramContents(0, 0, 0)
    } else {
      payload.instagram_contents = []
    }

    const simplePlatforms = [
      { field: 'facebook_contents', tactic: BrandPlanTactic.Facebook, tactics: socialTactics },
      { field: 'linkedin_contents', tactic: BrandPlanTactic.LinkedIn, tactics: socialTactics },
      { field: 'twitter_contents', tactic: BrandPlanTactic.Twitter, tactics: socialTactics },
      { field: 'tiktok_contents', tactic: BrandPlanTactic.TikTok, tactics: videoTactics },
      { field: 'youtube_contents', tactic: BrandPlanTactic.YouTube, tactics: videoTactics },
    ]

    simplePlatforms.forEach(({ field, tactic, tactics }) => {
      payload[field] = tactics.includes(tactic) ? formValues[field as keyof FormValues] : []
    })

    if (isBlogSelected && formValues.seo_contents?.some((c) => c.count > 0)) {
      payload.seo_contents = formValues.seo_contents
    }
    if (isEmailSelected && formValues.email_contents?.some((c) => c.count > 0)) {
      payload.email_contents = formValues.email_contents
    }

    return payload
  }

  const buildPlatformSelections = () => {
    const selections: any = {}
    if (isBlogSelected) {
      selections.selected_blog_platform = selectedBlogPlatform
    }
    if (isEmailSelected) {
      selections.selected_email_platform = selectedEmailPlatform
    }
    return selections
  }

  const handleSubmit = () => {
    updateBrandProfile({
      keywords: initialSeoKeywords,
      ideal_customer_profiles: initialIdealCustomers,
    })

    const platformSelections = buildPlatformSelections()
    const contentsPayload = buildContentsPayload()

    startOne()
    abortControllerRef.current = new AbortController()

    updateBrandPlan(
      {
        tactics,
        start_date: dayjs().add(2, 'day').format('YYYY-MM-DD'),
        ...(DEFAULT_SCHEDULE_CONFIG as any),
        crosspost_frequency: 'always',
        ...platformSelections,
        ...contentsPayload,
      },
      {
        onSuccess: async (updatedBrandPlan) => {
          queryClient.setQueryData(['workspaces', workspaceId, 'brandPlans', 'detail', brandPlan.id], updatedBrandPlan)

          try {
            await createBrandPlanPreparationSync({ signal: abortControllerRef.current?.signal })

            onNext(buildOnNextPayload())
          } catch (error) {
            onNext(buildOnNextPayload())
          }
        },
      },
    )
  }

  const isLoading = isUpdatingBrandPlan || isPreparing || isRunningOne || isRunningTwo || isRunningThree
  const isNextDisabled = tactics.length === 0 || isLoading

  const handleBack = () => {
    resetOne()
    resetTwo()
    resetThree()
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    onBack()
  }

  if (isPreparing || isRunningOne || isRunningTwo || isRunningThree) {
    return (
      <>
        <Loading
          showSupportButton={!isMobile}
          backgroundClassName={cx(
            AutopilotRegistrationAndOnboardingStyles.cloudBackground,
            BrandPlanTacticsStyles.loadingBackground,
          )}
          onCancel={handleBack}
          header="Blaze is creating your first batch of topics"
          description={undefined}
          progresses={[
            {
              title: 'Analyzing ideas',
              isRunning: isRunningOne,
              percentComplete: progressOne,
            },
            { title: 'Generating topics', isRunning: isRunningTwo, percentComplete: progressTwo },
            { title: 'Finishing up', isRunning: isRunningThree, percentComplete: progressThree },
          ]}
        />
      </>
    )
  }

  const categories = [
    {
      title: 'Social Media',
      credits: '~3 credits/post',
      countValue: contentCounts.social_media,
      onCountChange: (val: number) => updateCrosspostPlatformCounts(val, 'crosspost_contents', socialTactics),
      renderContent: () =>
        SOCIAL_PLATFORMS.map((platform) => (
          <PlatformCheckbox
            key={platform.tactic}
            {...platform}
            isSelected={socialTactics.includes(platform.tactic)}
            onToggle={handlePlatformToggle}
          />
        )),
    },
    {
      title: 'Short-form Video',
      credits: '~18 credits/post',
      countValue: contentCounts.short_form_video,
      onCountChange: (val: number) => updateCrosspostPlatformCounts(val, 'video_crosspost_contents', videoTactics),
      renderContent: () =>
        VIDEO_PLATFORMS.map((platform) => (
          <PlatformCheckbox
            key={platform.tactic}
            {...platform}
            isSelected={videoTactics.includes(platform.tactic)}
            onToggle={handlePlatformToggle}
          />
        )),
    },
    {
      title: 'Paid Ads',
      credits: '~3 credits/variant ad',
      customHeaderRight: (
        <WeeklyBudgetInput value={weeklyAdsBudget} onChange={(val) => setFieldValue('weekly_ads_budget', val)} />
      ),
      renderContent: () => (
        <MetaAdsCheckbox
          isSelected={paidAdsTactics.includes(BrandPlanTactic.MetaAds)}
          onChange={() => handlePlatformToggle(BrandPlanTactic.MetaAds)}
        />
      ),
    },
    {
      title: 'Blog',
      credits: '~9 credits per post',
      countValue: contentCounts.blog,
      onCountChange: (val: number) => setFieldValue('seo_contents', [{ type: 'blog_post', count: val }]),
      renderContent: () => (
        <CheckboxLabel
          className={Styles.platformCheckbox}
          isSelected={isBlogSelected}
          onChange={(selected) => setIsBlogSelected(selected)}
          aria-label="Blog"
        >
          <CheckboxBox size={20} color="var(--dark-80)" aria-label="Blog" />
          <PlatformSelectDropdown
            type="blog"
            isSelected={isBlogSelected}
            selectedPlatform={selectedBlogPlatform}
            defaultPlatform={PLATFORM_CONFIGS.blog.defaultPlatform}
            platforms={PLATFORM_CONFIGS.blog.platforms(wixEnabled, shopifyEnabled)}
            noneLabel="I don't want blog posts"
            onSelectionChange={(value) => {
              if (value === 'none') {
                setIsBlogSelected(false)
              } else {
                setIsBlogSelected(true)
                setSelectedBlogPlatform(value as SelectedBlogPlatform)
              }
            }}
          />
        </CheckboxLabel>
      ),
    },
    {
      title: 'Email',
      credits: '~3 credits per post',
      countValue: contentCounts.email,
      onCountChange: (val: number) => setFieldValue('email_contents', [{ type: 'newsletter', count: val }]),
      renderContent: () => (
        <CheckboxLabel
          className={Styles.platformCheckbox}
          isSelected={isEmailSelected}
          onChange={(selected) => setIsEmailSelected(selected)}
          aria-label="Email"
        >
          <CheckboxBox size={20} color="var(--dark-80)" aria-label="Email" />
          <PlatformSelectDropdown
            type="email"
            isSelected={isEmailSelected}
            selectedPlatform={selectedEmailPlatform}
            defaultPlatform={PLATFORM_CONFIGS.email.defaultPlatform}
            platforms={PLATFORM_CONFIGS.email.platforms()}
            noneLabel="I don't want emails"
            onSelectionChange={(value) => {
              if (value === 'none') {
                setIsEmailSelected(false)
              } else {
                setIsEmailSelected(true)
                setSelectedEmailPlatform(value as SelectedEmailPlatform)
              }
            }}
          />
        </CheckboxLabel>
      ),
    },
  ]

  return (
    <MultiStepStepLayout
      title="Set your Content Plan"
      description="Your plan includes 200 free credits per week. Select the platforms you want to generate content for, and how much content you'd like to generate per week."
      scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                <Button id="brand-plan-tactics-redesign-back" variant="tertiary" size="lg" onPress={handleBack}>
                  Back
                </Button>
              </ResponsiveDesign.Desktop>
              <div className={Styles.footerRight}>
                {/* Intentionally hidden - gotta check with Luke as I dont think this calc is right... */}
                {/* <button className={Styles.creditsButton} type="button">
                  <Text variant="primary" color="var(--dark-90)">
                    Total credits per week: {totalCredits}
                  </Text>
                  <ChevronDown size={12} color="var(--dark-90)" />
                </button> */}
                <Button
                  id="brand-plan-tactics-redesign-next"
                  size="lg"
                  onPress={handleSubmit}
                  isDisabled={isNextDisabled}
                  fullWidth={isMobile}
                >
                  Next, review topics
                </Button>
              </div>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.container}>
          {categories.map((category, index) => (
            <div key={index} className={Styles.categorySection}>
              <div className={Styles.categoryHeader}>
                <div className={Styles.categoryHeaderLeft}>
                  <Heading level={3}>{category.title}</Heading>
                  <Text variant="secondary" color="var(--dark-40)">
                    {category.credits}
                  </Text>
                </div>
                <div className={Styles.categoryHeaderRight}>
                  {category.customHeaderRight ||
                    (category.onCountChange && (
                      <>
                        <Text variant="secondary" color="var(--dark-40)">
                          Posts/week
                        </Text>
                        <CountInput
                          name={category.title.toLowerCase().replace(/\s+/g, '_')}
                          value={category.countValue || 0}
                          onChange={category.onCountChange}
                          min={0}
                          max={15}
                          size="sm"
                          buttonSize="sm"
                          applyRedesign
                        />
                      </>
                    ))}
                </div>
              </div>
              <div className={Styles.channelsGrid}>{category.renderContent()}</div>
            </div>
          ))}
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}

const BrandPlanTacticsRedesignContainer = ({
  id,
  tactics,
  seoKeywords,
  idealCustomers,
  mode,
  onBack,
  onNext,
}: BrandPlanTacticsRedesignContainerProps) => {
  const { data: brandPlan } = useBrandPlan(id)

  if (!brandPlan) {
    return null
  }

  return (
    <BrandPlanTacticsRedesign
      brandPlan={brandPlan}
      tactics={tactics}
      seoKeywords={seoKeywords}
      idealCustomers={idealCustomers}
      mode={mode}
      onBack={onBack}
      onNext={onNext}
    />
  )
}

export { BrandPlanTacticsRedesignContainer as BrandPlanTacticsRedesign }
